import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Star, Search, MapPin, ThumbsUp, TrendingUp, Clock, Award, ChevronRight, MessageSquare } from "lucide-react";

function Stars({ value, size = 15 }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} className={i <= Math.round(value) ? "text-[#F47B20] fill-[#F47B20]" : "text-slate-300"} />
      ))}
    </span>
  );
}

function BizCard({ b }) {
  return (
    <Link to={b.slug ? `/commerce/${b.slug}` : `/partenaires`} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 card-hover" data-testid="avis-biz-card">
      <div className="w-12 h-12 rounded-xl bg-[#172A3A] text-white grid place-items-center font-extrabold shrink-0 overflow-hidden">
        {b.logo ? <img src={b.logo} alt="" className="w-full h-full object-cover" /> : (b.name || "?")[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-[#172A3A] truncate">{b.name}</div>
        <div className="text-xs text-slate-500 truncate">{b.category || "Commerce"} · {b.city}</div>
        <div className="flex items-center gap-2 mt-1">
          <Stars value={b.average} />
          <span className="text-sm font-bold text-[#172A3A]">{b.average || "—"}</span>
          <span className="text-xs text-slate-400">({b.count} avis)</span>
          {b.recommendations > 0 && <span className="text-xs text-emerald-600 inline-flex items-center gap-0.5"><ThumbsUp size={11} /> {b.recommendations}</span>}
        </div>
      </div>
      <ChevronRight size={18} className="text-slate-300 shrink-0" />
    </Link>
  );
}

export default function AvisHub() {
  const { seg1, seg2 } = useParams();
  // /avis/:seg1/:seg2 => trade + city ; /avis/:seg1 => ville
  const presetCategory = seg2 ? seg1 : "";
  const presetCity = seg2 ? seg2 : (seg1 || "");

  const [home, setHome] = useState(null);
  const [cities, setCities] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState(presetCity);
  const [category, setCategory] = useState(presetCategory);
  const [sort, setSort] = useState("rating");
  const [results, setResults] = useState(null);

  const isSearch = useMemo(() => !!(q || city || category), [q, city, category]);

  useSeo({
    title: presetCity
      ? `Avis & recommandations ${presetCategory ? presetCategory + " " : ""}à ${presetCity} | PROXIreduc-28`
      : "Avis & Recommandations locales — Chartres & Eure-et-Loir | PROXIreduc-28",
    description: "Trouvez les meilleurs commerces, artisans et professionnels près de chez vous grâce aux avis des habitants de l'Eure-et-Loir. Notes, recommandations et expériences vérifiées.",
    canonicalPath: seg2 ? `/avis/${seg1}/${seg2}` : seg1 ? `/avis/${seg1}` : "/avis",
  });

  useEffect(() => {
    api.get("/avis/home").then((r) => setHome(r.data)).catch(() => {});
    api.get("/avis/cities").then((r) => setCities(r.data.cities || [])).catch(() => {});
    api.get("/avis/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, []);

  const runSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (city) p.set("city", city); if (category) p.set("category", category);
    p.set("sort", sort);
    api.get(`/avis/search?${p.toString()}`).then((r) => setResults(r.data.items || [])).catch(() => setResults([]));
  };
  useEffect(() => { if (isSearch) runSearch(); /* eslint-disable-next-line */ }, [sort]);
  useEffect(() => { if (presetCity || presetCategory) runSearch(); /* eslint-disable-next-line */ }, []);

  const SORTS = [["rating", "Mieux notés", Award], ["recommend", "Plus recommandés", ThumbsUp], ["count", "Plus commentés", MessageSquare], ["recent", "Récents", Clock]];

  return (
    <div className="min-h-screen bg-[#F2F4F5]" data-testid="avis-hub-page">
      {/* HERO + recherche */}
      <section className="bg-[#172A3A] text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Avis & Recommandations</h1>
          <p className="mt-2 text-white/80">Le bouche-à-oreille de Chartres et de l'Eure-et-Loir. Trouvez les meilleurs pros près de chez vous.</p>
          <div className="mt-6 bg-white rounded-2xl p-3 flex flex-col sm:flex-row gap-2" data-testid="avis-search-bar">
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search size={18} className="text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Plombier, restaurant, nom…" className="w-full h-11 outline-none text-sm text-slate-800" data-testid="avis-search-input" />
            </div>
            <select value={city} onChange={(e) => setCity(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-700" data-testid="avis-city-select">
              <option value="">Toutes villes</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-700 max-w-[160px]" data-testid="avis-cat-select">
              <option value="">Toutes activités</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <button onClick={runSearch} className="h-11 px-6 rounded-xl bg-[#F47B20] hover:bg-[#d96a16] text-white font-bold text-sm" data-testid="avis-search-btn">Rechercher</button>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {isSearch ? (
          <div data-testid="avis-results">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4">
              {SORTS.map(([k, label, Icon]) => (
                <button key={k} onClick={() => setSort(k)} data-testid={`avis-sort-${k}`}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${sort === k ? "bg-[#172A3A] text-white border-[#172A3A]" : "bg-white text-slate-600 border-slate-200"}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
            {results === null ? <div className="p-10 text-center text-slate-400">Chargement…</div>
              : results.length === 0 ? <div className="p-10 text-center text-slate-500">Aucun résultat. Essayez d'élargir votre recherche.</div>
              : <div className="grid sm:grid-cols-2 gap-3">{results.map((b) => <BizCard key={b.id} b={b} />)}</div>}
          </div>
        ) : home ? (
          <div className="space-y-10">
            <Section title="Les mieux notés" icon={Award} items={home.top_rated} />
            <Section title="Les plus recommandés" icon={ThumbsUp} items={home.most_recommended} />
            <Section title="Les plus commentés" icon={TrendingUp} items={home.most_reviewed} />
            {home.latest_reviews?.length > 0 && (
              <div data-testid="avis-latest">
                <h2 className="font-heading text-xl font-extrabold text-[#172A3A] mb-3 flex items-center gap-2"><Clock size={18} className="text-[#F47B20]" /> Derniers avis</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {home.latest_reviews.map((r) => (
                    <div key={r.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="avis-latest-card">
                      <div className="flex items-center justify-between"><Stars value={r.rating} /><span className="text-xs text-slate-400">{r.business_city}</span></div>
                      <div className="font-semibold text-[#172A3A] mt-1 text-sm">{r.business_name}</div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.comment || "—"}</p>
                      <div className="text-xs text-slate-400 mt-1">par {r.author}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {home.popular_categories?.length > 0 && (
              <div data-testid="avis-categories">
                <h2 className="font-heading text-xl font-extrabold text-[#172A3A] mb-3">Catégories populaires</h2>
                <div className="flex flex-wrap gap-2">
                  {home.popular_categories.map((c) => (
                    <button key={c.slug} onClick={() => { setCategory(c.slug); setTimeout(runSearch, 0); }} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700 hover:border-[#F47B20]" data-testid="avis-cat-chip">
                      {c.name} <span className="text-slate-400">({c.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : <div className="p-10 text-center text-slate-400">Chargement…</div>}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="avis-section">
      <h2 className="font-heading text-xl font-extrabold text-[#172A3A] mb-3 flex items-center gap-2"><Icon size={18} className="text-[#F47B20]" /> {title}</h2>
      <div className="grid sm:grid-cols-2 gap-3">{items.map((b) => <BizCard key={b.id} b={b} />)}</div>
    </div>
  );
}
